import { EVENT_RELATION_COUNTERS } from './relationship-integrity.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib — pure payload shaping applied BEFORE a JSON:API document
 *              reaches the store: drops relationship entries that would crash
 *              materialisation, and drops an EMPTY `event` relationship that
 *              travels without the counter that would vouch for it
 *   key:       sanitizeResourceRelationships(resource),
 *              dropUncountedEventRelationships(document), isEventType(type)
 *   uses:      lib/relationship-integrity (EVENT_RELATION_COUNTERS)
 *   siblings:  serializers/application.js (normalizeResponse / pushPayload /
 *              normalize call into here), controllers/application.js
 *              (updateSocketRecord sanitises the socket resource)
 *   tests:     payload-relationships-test.js
 *   gotchas:   these run on the RAW payload, before keyForAttribute /
 *              keyForRelationship, so the keys are the underscored ones the API
 *              sends (`total_contacts_number`, `related_events`) — which is
 *              exactly how EVENT_RELATION_COUNTERS is written. Dropping a
 *              relationship KEY is not the same as emptying it: an absent key
 *              leaves the cached relationship untouched, `data: []` deletes it.
 *              That asymmetry is the whole point of this module.
 *
 * WHY THIS EXISTS (POP-154 / Sentry EMBER-MEDICAL-2WW)
 * An appointment save sends every relationship as a complete list and
 * `update_event_all_v1` deletes the existing links before re-inserting exactly
 * what arrived — so a client whose relationship cache was emptied does not save
 * a partial appointment, it erases one. `_refuseStaleEventSave` catches that and
 * refuses; 68 refusals over 35 tenants in 30 days say the cache gets emptied
 * often. Measured over the 60 events Sentry kept: in 58 of them the REMOTE side
 * of the relationship in the client cache was empty while the counter next to it
 * said otherwise. An explicit `data: []` in a response is the only thing that
 * produces that — it zeroes the remote side and leaves the attributes intact.
 *
 * So the client must stop accepting a document that empties a relationship
 * without also carrying the counter that would vouch for the emptiness. The
 * server half (stop producing it) is POP-160, in the API/DB repos; this half
 * holds even if that one ships later.
 */

/**
 * Is this JSON:API `type` the event model? Raw payloads say `events`, a
 * normalized resource says `event`, and `store.normalize` hands us the
 * modelClass instead of a type at all.
 *
 * @param {string} type
 * @returns {boolean}
 */
function isEventType(type) {
  const value = String(type ?? '').toLowerCase().replace(/-/g, '_');
  return value === 'event' || value === 'events';
}
function isEmptyRelationshipData(data) {
  if (data === null) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

/**
 * One identifier entry is usable only when it carries BOTH a type and an id.
 * A null `type` is what takes the app down: ember-data hands it to
 * `normalizeModelName`, which calls `.replace` on it inside the dasherize cache
 * (Sentry EMBER-MEDICAL-2SF / 2VH / 2T9, `Cannot read properties of null`).
 */
function isUsableEntry(entry) {
  return Boolean(entry) && entry.type !== null && entry.type !== undefined && entry.id !== null && entry.id !== undefined;
}

/**
 * Drop every relationship of a resource that carries an unusable identifier
 * entry, and every falsy entry of `included`.
 *
 * The whole relationship KEY goes, not just the bad entry: a relationship we
 * cannot trust must leave the cached one alone, and filtering the bad entries
 * out would hand the store a shorter list — or an empty one — which ember-data
 * applies as a deletion. That is the very loss this module exists to prevent.
 *
 * The throw it avoids is not cosmetic: `JSONAPICache.upsert` writes the
 * ATTRIBUTES first and the relationships second, so a throw in the middle leaves
 * the record half-applied — new attributes, stale or absent relationships —
 * which is a client that now lies about itself (POP-154 §7).
 *
 * @param {Object} resource - a JSON:API resource object (or a document; both are
 *        walked, `data` and `included` included)
 * @returns {Object} the same object when there was nothing to drop, a shallow
 *          copy otherwise. Never mutates its argument.
 */
function sanitizeResourceRelationships(resource) {
  if (!resource || typeof resource !== 'object' || Array.isArray(resource)) {
    return resource;
  }
  let out = resource;
  const relationships = resource.relationships;
  if (relationships && typeof relationships === 'object') {
    const bad = Object.keys(relationships).filter(key => {
      const data = relationships[key]?.data;
      if (Array.isArray(data)) return data.some(entry => !isUsableEntry(entry));
      // A to-one with `data: null` is a legitimate "no related record"; only a
      // present-but-unusable object is a problem.
      if (data && typeof data === 'object') return !isUsableEntry(data);
      return false;
    });
    if (bad.length) {
      const kept = {
        ...relationships
      };
      for (const key of bad) delete kept[key];
      out = {
        ...out,
        relationships: kept
      };
    }
  }
  if (Array.isArray(resource.included)) {
    const included = resource.included.filter(Boolean).map(sanitizeResourceRelationships);
    if (included.length !== resource.included.length || included.some((r, i) => r !== resource.included[i])) {
      out = {
        ...out,
        included
      };
    }
  }
  return out;
}

/**
 * Drop an `event` relationship that arrives EMPTY without the
 * `total_<relation>_number` attribute that would vouch for the emptiness.
 *
 * `extractAttributes` copies only the keys a payload carries, while a named
 * relationship is REPLACED wholesale. So a read that names `contacts: []` and
 * says nothing about `total_contacts_number` leaves the record holding the
 * emptiness of one response next to the counter of another — a record that
 * contradicts itself, built with no error anywhere (POP-154 §7, §14.3).
 *
 * Only the EMPTY case is dropped, deliberately:
 *  - a relationship that arrives with members is data the payload legitimately
 *    owns. `events_data_v1` (the index) emits `contacts`/`products` and, on
 *    older revisions, no counter for them; dropping those outright would blank
 *    the patient names on every event list for records never opened in detail.
 *    Keeping them can still truncate a fuller list (the index caps at 10 and
 *    filters by role — POP-154 §14.2 measured `contacts 1/2`), which is a
 *    smaller, visible, reload-able problem than an empty one that also blocks
 *    the save;
 *  - an empty relationship WITH its counter is the server saying "there are
 *    none", and must go through: that is how removing the last treatment from an
 *    appointment reaches the store. `event_find_data_v2` (the detail) emits all
 *    seven counters, so a detail read is never touched here.
 *
 * @param {Object} document - a JSON:API document (`data` object or array,
 *        optional `included`) or a bare resource
 * @returns {Object} the same object when nothing was dropped, a shallow copy
 *          otherwise. Never mutates its argument.
 */
function dropUncountedEventRelationships(document) {
  if (!document || typeof document !== 'object') {
    return document;
  }
  let out = document;
  if (Array.isArray(document.data)) {
    const data = document.data.map(pruneEventResource);
    if (data.some((r, i) => r !== document.data[i])) out = {
      ...out,
      data
    };
  } else if (document.data && typeof document.data === 'object') {
    const data = pruneEventResource(document.data);
    if (data !== document.data) out = {
      ...out,
      data
    };
  } else if (document.relationships || document.attributes) {
    // A bare resource, as `store.normalize` and the socket path hand it over.
    return pruneEventResource(document);
  }
  if (Array.isArray(document.included)) {
    const included = document.included.map(entry => entry ? pruneEventResource(entry) : entry);
    if (included.some((r, i) => r !== document.included[i])) out = {
      ...out,
      included
    };
  }
  return out;
}

/**
 * The per-resource half of `dropUncountedEventRelationships`.
 * @private
 */
function pruneEventResource(resource) {
  if (!resource || typeof resource !== 'object' || !isEventType(resource.type)) {
    return resource;
  }
  const relationships = resource.relationships;
  if (!relationships || typeof relationships !== 'object') {
    return resource;
  }
  const attributes = resource.attributes ?? {};
  const uncounted = Object.entries(EVENT_RELATION_COUNTERS).filter(([relation, counterKey]) => {
    if (!Object.prototype.hasOwnProperty.call(relationships, relation)) return false;
    // `hasOwnProperty`, not a truthiness test: a counter of 0 is the answer, not
    // a missing one, and `undefined` under an existing key says nothing either.
    const counted = Object.prototype.hasOwnProperty.call(attributes, counterKey) && attributes[counterKey] !== undefined;
    return !counted && isEmptyRelationshipData(relationships[relation]?.data);
  });
  if (!uncounted.length) {
    return resource;
  }
  const kept = {
    ...relationships
  };
  for (const [relation] of uncounted) delete kept[relation];
  return {
    ...resource,
    relationships: kept
  };
}

export { dropUncountedEventRelationships, isEventType, sanitizeResourceRelationships };
//# sourceMappingURL=payload-relationships.js.map
